<template>
  <component :is="tag" class="landing-region-seo">
    <div class="section-inner">
      <template v-for="section in blok.body" :key="section._uid">
        <StoryblokComponent :blok="section" />
      </template>
      <div id="landing-region-seo-facilities">
        
        
        <FacilityCards
          :list="facilities"
          :include-result-number="true"
          :include-facility-type-group-key="type"
          :sort-alphabetically="sortAlphabetically === 'true'"
        />
      </div>
    </div>
  </component>
</template>

<script setup>
const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  tag: {
    type: String,
    required: false,
    default: "div",
  },
  area: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  type: {
    type: Array,
    required: false,
    default: () => {
      return [];
    },
  },
});

const route = useRoute();
const config = useRuntimeConfig();
const datasourcesStore = useDatasourcesStore();
const nuxtApp = useNuxtApp();
const countryPrefix = config.public["storyblokCountryPrefix"];

// Track Storyblok requests
const storyblokRequestCount = ref(0);
const trackedRequests = ref([]);

// Wrapper to track Storyblok requests
const trackedUseCustomAsyncStoryblok = async (url = "", apiOptions = {}, bridgeOptions = {}) => {
  const requestKey = `${url}${JSON.stringify(apiOptions)}`;
  storyblokRequestCount.value++;
  trackedRequests.value.push(requestKey);
  //console.log(`[Storyblok Request #${storyblokRequestCount.value}] ${url || 'stories'}`, apiOptions);
  return useCustomAsyncStoryblok(url, apiOptions, bridgeOptions);
};

// Get Facilities by Region
const slug = `${countryPrefix}${route.path}`;

const currentPage = computed(() => parseInt(route.query.page) || 1);
const isAreaSelected = props.area.area_type && props.area.area_value;

const minNavHeight = computed(() => {
  return document.querySelector(".min-nav")?.offsetHeight;
});

const manageFocus = () => {
  const content = document.querySelector("#landing-region-seo-facilities");

  if (!content) return;

  const resetFocus = () => {
    content.setAttribute("tabindex", "0");
    content.focus();
    content.removeAttribute("tabindex");
  };

  resetFocus();
  window.scrollTo({
    top:
      content.getBoundingClientRect().top + window.scrollY - minNavHeight.value,
    behavior: "smooth",
  });
};

const isDebugVisible = computed(() => {
  return String(route.query.debug).toLowerCase() === "true";
});

const PER_PAGE = 4;
// Use a stable cache-buster for the whole render (avoid generating a new useState key on each call)
const STORYBLOK_CV = Date.now();

const buildFetchFacilitiesParams = (page = 1) => ({
  version: config.public["storyblokVersion"],
  //starts_with: slug,
  starts_with: isAreaSelected
        ? config.public["storyblokCountryPrefix"]
        : slug,
  //content_type: "page-nursing-home",
  filter_query: areaFilter(),
  per_page: PER_PAGE,
  page,
  cv: STORYBLOK_CV,
});

const showDebugOverlay = (error) => {
  if (typeof window === "undefined" || !isDebugVisible.value) return;

  const areaType = props.area?.area_type ?? "";
  const areaValue = props.area?.area_value ?? "";
  const areaLevel = props.area?.area_level ?? "";
  const fetchFacilitiesParams = buildFetchFacilitiesParams();

  let errorBlock = "";
  const status = error && error.response && error.response.status;
  if (status === 429) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      JSON.stringify((error.response && error.response.data) || {}, null, 2);
    errorBlock = `<br><b>Error:</b> 429 Too Many Requests<br><pre style="margin:0;">${message}</pre>`;
  }

  nextTick(() => {
    let debugDiv = document.getElementById("debug-info-landing-region-seo");
    if (!debugDiv) {
      debugDiv = document.createElement("div");
      debugDiv.id = "debug-info-landing-region-seo";
      debugDiv.style =
        "position:fixed;bottom:10px;right:10px;z-index:10000;background:#282c34;color:#fff;padding:16px 24px;border-radius:6px;box-shadow:0 2px 16px rgba(0,0,0,0.2);font-size:14px;max-width:400px;white-space:pre-wrap;";
      document.body.appendChild(debugDiv);
    }
    debugDiv.innerHTML = `
      <b>Debug information</b><br>
      <b>area_type:</b> ${areaType}<br>
      <b>area_value:</b> ${areaValue}<br>
      <b>area_level:</b> ${areaLevel}<br>
      <b>Fetch Facilities Params:</b><br>
      <pre style="margin:0;">${JSON.stringify(fetchFacilitiesParams, null, 2)}</pre>
      <b>Number of query retries:</b> ${attempt}<br>
      ${errorBlock}
    `;
  });
};

 const settingsPromise = nuxtApp.runWithContext(() =>
    datasourcesStore.fetchCountrySettings()
  );

const serverSideFetchFacilities = async () => {
  // Fetch countrySettings in parallel, but fetch facilities page-by-page until there are no more results
 

  let allFacilities = [];
  let pageNum = 1;
  let lastError = null;

  while (true) {
    const params = buildFetchFacilitiesParams(pageNum);

    // Nuxt context can be lost across async loops; keep composables in-context
    const facilitiesResponse = await nuxtApp.runWithContext(() =>
      trackedUseCustomAsyncStoryblok("", params)
    );

    // @ts-ignore - error is attached in useCustomAsyncStoryblok
    const facilitiesError = facilitiesResponse?.error;
    if (facilitiesError) {
      lastError = facilitiesError;
      break;
    }

    const facilitiesPage = facilitiesResponse?.value?.stories ?? [];
    if (facilitiesPage.length === 0) break;

    allFacilities = allFacilities.concat(facilitiesPage);

    // Stop when we get a partial page (no need to fetch one extra empty page)
    if (facilitiesPage.length < PER_PAGE) break;

    pageNum += 1;
  }

  await settingsPromise;

  if (lastError && typeof window !== "undefined") {
    showDebugOverlay(lastError);
  }

  const facilitiesResponse = { value: { stories: allFacilities } };

  try {
    const allowedContentTypes = datasourcesStore.getFacilityPageContentTypes;
    const facilities = facilitiesResponse.value?.stories?.filter((item) => allowedContentTypes.includes(item.content?.component))
      ?.map((item) => ({
        address: item.content?.address,
        addressSupplement: null,
        city: item.content?.city,
        facilityId: item.content?.facilityId,
        facilityType: item.content?.centerType,
        facilityUniqueKey: item.content?.facilityUniqueKey,
        image:  item.content?.body?.[0]?.images?.[0]?.asset?.filename || "",
        name: item.content?.facility_title || item.content?.facility_h1 || item.content?.commercialName,
        slug: item.full_slug,
        zipCode: item.content?.postalCode,
        facilityTypeGroupKey: item.content?.facilityTypeGroupKey,
        webMarketingRank: item.content?.webMarketingRank
      }));
    return {
      facilities: facilities || [],
    };
  } catch (error) {
    console.log(error);
    return {
      facilities: [],
    };
  }
};

const matchesKebabCaseCondition = (value) => {
  return value.includes("'") ||
    value.includes(" ") ||
    value.includes("-") ||
    /[A-Z\u00C0-\u017F]/.test(value);
};

const areaFilter = () => {
  const allowedContentTypes = datasourcesStore.getFacilityPageContentTypes;
  let filter = {};
  const areaType = props.area?.area_type;
  const areaValue = String(props.area?.area_value ?? "");
  if(props.type?.length) {
    let types;
    props.type.forEach((item) => {
      types = types !== "" && types !== undefined ? `${types},${item}` : `${item}`;
    });
    
    filter.facilityTypeGroupKey = {
      in: types,
    };
  }

  switch (areaType) {
    case "city":
      if (areaValue.includes("'")) {
        filter.address_search_slug_city = {
          in: useKebabCaseFilter(areaValue),
        };
      } else {
        filter.city = {
          in: areaValue,
        };
      }
      break;

    case "postalCode":
      filter.postalCode = {
        in: areaValue,
      };
      break;

    case "district":
    // Check if area_value includes "'" or "-", or contains a capital letter
      if (matchesKebabCaseCondition(areaValue)) {
        filter.address_search_slug_district = {
          in: useKebabCaseFilter(areaValue),
        };
      } else { 
        filter.district = {
          in: areaValue,
        };
      }
      break;

    case "departCode":
      filter.departCode = { 
        in: props.area?.area_value,
      };
      break;

    case "region":
    if (
        matchesKebabCaseCondition(areaValue)
      ) {
        filter.address_search_slug_district = {
          in: useKebabCaseFilter(props.area?.area_value),
        };
      } else { 
        filter.region = {
          in: props.area?.area_value,
        };
      }
      break;

  }
  filter.component = {
    in: allowedContentTypes.join(',')
  };

  // Always add address_search_slug_district filter with area_value, regardless of area_type (if area_value present)
/*  if (props.area?.area_value) {
    filter.address_search_slug_district = {
      in: props.area?.area_value,
    };
  }
*/
  return filter;
};

// Show debug overlay on client when ?debug=true
onMounted(() => {
  showDebugOverlay();
});

let facilities;
let attempt = 1;
const maxRetries = 4;
const retryDelay = 500; // ms

while (attempt < maxRetries) {
  try {
    ({ facilities } = await serverSideFetchFacilities());
    // Retry if zero facilities found, unless it's last attempt
    if (facilities && facilities.length === 0 && attempt < maxRetries - 1) {
      attempt++;
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      continue;
    }
    break; // success or finished retrying zero facilities, exit loop
  } catch (err) {
    attempt++;
    if (attempt >= maxRetries) {
      throw err;
    }
    await new Promise(resolve => setTimeout(resolve, retryDelay));
  }
}
const sortAlphabetically = datasourcesStore.findCountrySetting('facility.list.alphabetical.order');

// Log Storyblok request count after component is ready
// Note: fetchCountrySettings() also makes a Storyblok request via useCustomAsyncStoryblok,
// but it's not tracked here since it's called from the store. Add +1 if fetchCountrySettings wasn't cached.
onMounted(() => {
  const totalRequests = storyblokRequestCount.value;
  console.log(`[LandingRegionSeo] Tracked Storyblok requests: ${totalRequests}`);
  console.log(`[LandingRegionSeo] Request details:`, trackedRequests.value);
  console.log(`[LandingRegionSeo] Note: fetchCountrySettings() makes +1 additional request (if not cached)`);
});

watch(currentPage, manageFocus);
</script>

<style lang="scss" scoped>
.landing-region-seo {
  .section-inner > *:not(:first-child) {
    padding-top: 4rem;
  }
  .results-number {
    margin-bottom: 0.75rem;
  }
}
#landing-region-seo-facilities {
  :deep(.results.section) {
    display: inherit;
    padding-top: 4rem;
  }
}
</style>
